import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Menu } from "lucide-react";
import { toast } from "sonner";

import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import Composer from "../components/Composer";
import MessageList from "../components/MessageList";
import EmptyState from "../components/EmptyState";
import ChangePasswordModal from "../components/ChangePasswordModal";
import useStreamingChat from "../hooks/useStreamingChat";
import useMediaQuery from "../hooks/useMediaQuery";
import StellaLogo from "../components/StellaLogo";

export default function Chat() {
  const { chatId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chat, setChat] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [drawer, setDrawer] = useState(false);

  const [composerText, setComposerText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamingStatus, setStreamingStatus] = useState("");
  const [streamingChip, setStreamingChip] = useState(null);
  const [brainState, setBrainState] = useState(null);   // {phase, agents, review, escalated}
  const [voiceStatus, setVoiceStatus] = useState(null);
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("stella_tts") === "1";
  });
  const scrollRef = useRef(null);

  // Per-composer default settings. Life Assistant is the new default.
  const [mode, setMode] = useState("life_assistant");
  const [context, setContext] = useState("general");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-4.1");
  const [projectId, setProjectId] = useState(null);

  const { send, stop } = useStreamingChat();
  const isDesktop = useMediaQuery("(min-width: 640px)");

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/chats");
      setChats(data);
    } catch {}
  }, []);
  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch {}
  }, []);
  const loadRegistry = useCallback(async () => {
    try {
      const { data } = await api.get("/models");
      setRegistry(data);
    } catch {}
  }, []);
  const loadVoiceStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/voice/status");
      setVoiceStatus(data);
    } catch {
      setVoiceStatus(null);
    }
  }, []);

  useEffect(() => {
    loadChats();
    loadProjects();
    loadRegistry();
    loadVoiceStatus();
  }, [loadChats, loadProjects, loadRegistry, loadVoiceStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("stella_tts", ttsEnabled ? "1" : "0");
  }, [ttsEnabled]);

  // Load messages when chatId changes.
  useEffect(() => {
    (async () => {
      if (!chatId) {
        setMessages([]); setChat(null);
        return;
      }
      try {
        const [{ data: c }, { data: msgs }] = await Promise.all([
          api.get(`/chats/${chatId}`),
          api.get(`/chats/${chatId}/messages`),
        ]);
        setChat(c);
        setMessages(msgs);
        setMode(c.mode); setContext(c.context);
        setProvider(c.provider); setModel(c.model);
        setProjectId(c.project_id || null);
      } catch (err) {
        toast.error(errText(err, "Could not load chat"));
        nav("/", { replace: true });
      }
    })();
  }, [chatId, nav]);

  // Consume Today deep-link params (?prompt=&mode=)
  useEffect(() => {
    if (chatId) return;
    const p = searchParams.get("prompt");
    const m = searchParams.get("mode");
    if (p) setComposerText(p);
    if (m) setMode(m);
    if (p || m) {
      // clear query to avoid re-triggering
      const next = new URLSearchParams(searchParams);
      next.delete("prompt"); next.delete("mode");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // Auto-scroll on stream.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText, streaming]);

  const startNewChat = useCallback(async () => {
    setDrawer(false);
    nav("/", { replace: true });
  }, [nav]);

  const ensureChat = useCallback(async () => {
    if (chatId) return chatId;
    const { data } = await api.post("/chats", {
      title: "New chat", provider, model, mode, context,
      project_id: context === "project" ? projectId : null,
    });
    setChat(data);
    await loadChats();
    nav(`/c/${data.id}`, { replace: true });
    return data.id;
  }, [chatId, provider, model, mode, context, projectId, loadChats, nav]);

  const sendMessage = useCallback(async (text, { editMessageId = null, regenerate = false } = {}) => {
    if (!text && !regenerate) return;
    const id = await ensureChat();

    if (!regenerate && !editMessageId) {
      const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
      setMessages((prev) => [...prev, optimistic]);
    } else if (editMessageId) {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === editMessageId);
        if (idx < 0) return prev;
        const kept = prev.slice(0, idx);
        return [...kept, { ...prev[idx], content: text }];
      });
    } else if (regenerate) {
      setMessages((prev) => {
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].role === "assistant") { const c = prev.slice(); c.splice(i, 1); return c; }
        }
        return prev;
      });
    }

    setStreaming(true);
    setStreamingText("");
    setStreamingStatus("");
    setStreamingChip(null);
    setBrainState(null);
    let acc = "";

    await send({
      chatId: id,
      body: {
        content: text || "",
        provider, model, mode, context,
        project_id: context === "project" ? projectId : null,
        edit_message_id: editMessageId,
        regenerate,
        attachment_ids: attachments.map((a) => a.id),
      },
      handlers: {
        onDelta: (chunk) => {
          acc += chunk;
          setStreamingText(acc);
        },
        onStatus: (data) => {
          if (data.phase === "routing") setStreamingStatus(`Routed to ${data.persona}`);
          if (data.phase === "reviewing") setStreamingStatus("Reviewing draft...");
        },
        onReviewPath: (data) => {
          toast.message("Orchestrated Review", { description: data.line });
        },
        onChip: (data) => {
          setStreamingChip(data);
        },
        onBrainStatus: (data) => {
          const label = {
            routing: "Using Stella Brain — classifying…",
            planning: "Using Stella Brain — planning…",
            agents: "Using Stella Brain — consulting specialists…",
            critic: "Using Stella Brain — reviewing…",
            synth: "Using Stella Brain — composing final answer…",
          }[data.phase] || "Using Stella Brain";
          setStreamingStatus(label);
        },
        onBrainAgents: (data) => {
          setBrainState((prev) => ({ ...(prev || {}), agents: data.labels || data.agents,
                                     task_type: data.task_type, risk_tier: data.risk_tier,
                                     escalated: !!data.auto_escalated }));
        },
        onBrainReview: (data) => {
          setBrainState((prev) => ({ ...(prev || {}), review: data.line,
                                     escalated: !!data.auto_escalated }));
        },
        onBrainBypass: () => {
          // Simple request — no brain layers used; drop the brain status.
          setStreamingStatus("");
          setBrainState(null);
        },
        onBrainDone: (data) => {
          setBrainState((prev) => ({ ...(prev || {}), completed: true, run_id: data?.run_id,
                                     total_ms: data?.total_ms }));
        },
        onDone: async () => {
          setStreaming(false); setStreamingText(""); setStreamingStatus(""); setStreamingChip(null);
          setBrainState(null);
          const { data: refreshed } = await api.get(`/chats/${id}/messages`);
          setMessages(refreshed);
          await loadChats();
        },
        onAbort: async () => {
          setStreaming(false); setStreamingText(""); setStreamingStatus(""); setStreamingChip(null);
          setBrainState(null);
          const { data: refreshed } = await api.get(`/chats/${id}/messages`);
          setMessages(refreshed);
        },
        onError: (err) => {
          setStreaming(false); setStreamingText(""); setStreamingStatus(""); setStreamingChip(null);
          setBrainState(null);
          if (typeof err?.message === "string" && err.message.endsWith("_setup_required")) {
            toast.error("Provider setup required", { description: `Add the API key for ${err.message.replace("_setup_required", "")}.` });
          } else {
            toast.error(err?.message || "Stream error");
          }
        },
      },
    });
  }, [ensureChat, provider, model, mode, context, projectId, send, loadChats, attachments]);

  const handleSend = (text) => {
    setComposerText("");
    // Snapshot + clear attachments before send so they only apply to this turn.
    setAttachments([]);
    sendMessage(text);
  };

  const handleRegenerate = () => sendMessage("", { regenerate: true });
  const handleEditSubmit = (id, text) => sendMessage(text, { editMessageId: id });

  const onPickModel = (p, m) => { setProvider(p); setModel(m); };

  const showChangePassword = user?.must_change_password;

  return (
    <div className="h-screen w-screen flex bg-[#FAF9F5] overflow-hidden">
      {isDesktop && (
        <div className="flex sm:w-72 shrink-0">
          <Sidebar
            chats={chats}
            projects={projects}
            activeChatId={chatId}
            onSelectChat={(id) => nav(`/c/${id}`)}
            onNewChat={startNewChat}
            onRefreshChats={loadChats}
            onRefreshProjects={loadProjects}
          />
        </div>
      )}

      {!isDesktop && drawer && (
        <div className="fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72">
            <Sidebar
              chats={chats}
              projects={projects}
              activeChatId={chatId}
              onSelectChat={(id) => { nav(`/c/${id}`); setDrawer(false); }}
              onNewChat={() => { startNewChat(); setDrawer(false); }}
              onRefreshChats={loadChats}
              onRefreshProjects={loadProjects}
              onClose={() => setDrawer(false)}
            />
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-[#E5E4DE] bg-[#FAF9F5]/95 backdrop-blur-md flex items-center gap-2 px-3 sm:px-5 shrink-0">
          <button
            onClick={() => setDrawer(true)}
            className="sm:hidden p-2 rounded-lg hover:bg-slate-200/60"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex items-center gap-2 text-indigo-700 sm:hidden">
            <StellaLogo className="w-5 h-5" />
            <span className="font-bold text-slate-900">Stella</span>
          </div>
          <div className="hidden sm:block flex-1 truncate text-sm text-slate-700 font-medium">
            {chat?.title || "New chat"}
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="chat-scroll">
          {(!chatId || messages.length === 0) && !streaming ? (
            <EmptyState userName={user?.email?.split("@")[0] || "there"} onPick={(t) => setComposerText(t)} />
          ) : (
            <MessageList
              messages={messages}
              streaming={streaming}
              streamingText={streamingText}
              streamingStatus={streamingStatus}
              streamingChip={streamingChip}
              brainState={brainState}
              onRegenerate={handleRegenerate}
              onEditSubmit={handleEditSubmit}
              onRefresh={loadChats}
              ttsEnabled={ttsEnabled}
              voiceStatus={voiceStatus}
            />
          )}
        </div>

        <div className="shrink-0">
          <Composer
            value={composerText}
            onChange={setComposerText}
            onSend={handleSend}
            onStop={stop}
            streaming={streaming}
            mode={mode} setMode={setMode}
            context={context} setContext={setContext}
            provider={provider} model={model}
            registry={registry} onPickModel={onPickModel}
            projects={projects}
            projectId={projectId} setProjectId={setProjectId}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            voiceStatus={voiceStatus}
            ttsEnabled={ttsEnabled}
            setTtsEnabled={setTtsEnabled}
          />
        </div>
      </main>

      <ChangePasswordModal open={!!showChangePassword} />
    </div>
  );
}
